"use client";

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../components/catalyst/button";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import clsx from "clsx";
import { ChevronLeftIcon, InboxArrowDownIcon } from "@heroicons/react/20/solid";
import { useToast } from "../../../util/ToastProvider";
import { Dialog } from "@headlessui/react";

export default function AnnexureForm() {
  const db = getFirestore();
  const toaster = useToast();
  const functions = getFunctions();
  const saveFn = httpsCallable(functions, "templateManager");

  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    number: "",
    title: "",
    requiresConsent: null as boolean | null,
    body: "",
  });

  const [initialForm, setInitialForm] = useState<any>(null);
  const [errors, setErrors] = useState<{ number?: string; title?: string }>({});
  const [loading, setLoading] = useState(false);

  const [discardOpen, setDiscardOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDirty =
    initialForm &&
    (initialForm.number !== form.number ||
      initialForm.title !== form.title ||
      initialForm.requiresConsent !== form.requiresConsent ||
      initialForm.body !== form.body);

  const isFormValid = () => {
    return (
      form.number.trim().length > 0 &&
      form.title.trim().length > 0 &&
      form.body.trim().length > 0 &&
      typeof form.requiresConsent === "boolean" &&
      !errors.number &&
      !errors.title
    );
  };

  // Prefill when editing
  useEffect(() => {
    if (!isEdit) {
      setInitialForm(form);
      return;
    }

    (async () => {
      const snap = await getDoc(doc(db, "annexures", "meta", "data", id!));
      if (snap.exists()) {
        const d: any = snap.data();
        const loaded = {
          number: d.number || "",
          title: d.title || "",
          requiresConsent: d.consent ?? null,
          body: d.instructions || "",
        };
        setForm(loaded);
        setInitialForm(loaded);
      }
    })();
  }, [id, isEdit, db]);

  function validate() {
    const e: any = {};

    if (!form.number.trim()) e.number = "Number is required";
    else if (form.number.trim().length < 3) e.number = "Minimum 3 characters";
    else if (!/^[A-Z][0-9]+/.test(form.number))
      e.number = "Format example: L27";

    if (!form.title.trim()) e.title = "Title is required";
    else if (form.title.trim().length < 3) e.title = "Minimum 3 characters";

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;

    setLoading(true);
    try {
      await saveFn({
        action: "saveAnnexure",
        id: id || null,
        number: form.number,
        title: form.title,
        instructions: form.body,
      });
      toaster.success("Annexure saved successfully");
      navigate("/admin/annexures");
    } catch (err: any) {
      const code = String(err.code || "").split("functions/")[1] || "unknown";
      const msg = err.message || "Something went wrong";

      if (code === "already-exists") {
        if (msg.includes("number")) {
          setErrors((prev) => ({
            ...prev,
            number: "This number is already used",
          }));
        }
        if (msg.includes("title")) {
          setErrors((prev) => ({
            ...prev,
            title: "This title is already used",
          }));
        }
      }

      toaster.error(msg, 5000);
    } finally {
      setLoading(false);
    }
  }

  function handleCancelOrBack() {
    if (isDirty) setDiscardOpen(true);
    else navigate("/admin/annexures");
  }

  return (
    <div className={`p-8 ${loading ? "opacity-60 pointer-events-none" : ""}`}>
      {/* BACK BUTTON */}
      <button
        onClick={handleCancelOrBack}
        className="inline-flex items-center gap-2 text-sm text-gray-500"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        <span>{isEdit ? `${form.number}: ${form.title}` : "Annexures"}</span>
      </button>

      <div className="mt-4 mb-6 flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {isEdit ? "Edit annexure" : "Add annexure"}
        </h1>

        <div className="pointer-events-auto flex gap-4">
          <button
            onClick={handleCancelOrBack}
            className="rounded-lg border border-zinc-300 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
          >
            Cancel
          </button>

          <Button
            color="brand"
            disabled={!isFormValid() || loading}
            onClick={handleSave}
          >
            <InboxArrowDownIcon className="h-6 w-6 text-white" />
            {loading ? "Saving..." : isEdit ? "Done" : "Save"}
          </Button>
        </div>
      </div>

      {/* FORM FIELDS */}
      <div className="flex gap-6">
        {/* NUMBER FIELD */}
        <div className="basis-[30%]">
          <label className="block text-sm font-medium mb-1">Number *</label>

          <input
            maxLength={8}
            disabled={loading}
            className={clsx(
              "w-full border rounded-lg p-2 disabled:opacity-50 disabled:cursor-not-allowed",
              errors.number ? "border-red-400" : "border-gray-300"
            )}
            value={form.number}
            onChange={(e) => {
              let val = e.target.value.replace(/\s+/g, "").slice(0, 8);
              setErrors((prev) => ({ ...prev, number: undefined }));
              setForm({ ...form, number: val.toUpperCase() });
            }}
          />

          {errors.number && (
            <div className="text-xs text-red-500 mt-1">{errors.number}</div>
          )}

          {!errors.number && (
            <div
              className={clsx(
                "text-xs mt-1",
                !form.number.trim() ? "text-red-500" : "text-gray-500"
              )}
            >
              Must be unique
            </div>
          )}
        </div>

        {/* TITLE FIELD */}
        <div className="basis-[70%]">
          <label className="block text-sm font-medium mb-1">Title *</label>

          <input
            maxLength={50}
            disabled={loading}
            className={clsx(
              "w-full border rounded-lg p-2 disabled:opacity-50 disabled:cursor-not-allowed",
              errors.title ? "border-red-400" : "border-gray-300"
            )}
            value={form.title}
            onChange={(e) => {
              let val = e.target.value.slice(0, 50);
              setErrors((prev) => ({ ...prev, title: undefined }));
              setForm({ ...form, title: val });
            }}
          />

          {errors.title && (
            <div className="text-xs text-red-500 mt-1">{errors.title}</div>
          )}

          {!errors.title && (
            <div
              className={clsx(
                "text-xs mt-1",
                !form.title.trim() ? "text-red-500" : "text-gray-500"
              )}
            >
              Must be unique. Visible to members.
            </div>
          )}
        </div>
      </div>

      {/* CONSENT */}
      <div className="mt-8">
        <label className="block text-sm font-medium mb-1">
          Requires member's consent *
        </label>

        <div className="flex gap-6 items-center mt-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="consent"
              checked={form.requiresConsent === false}
              onChange={() => setForm({ ...form, requiresConsent: false })}
            />
            No
          </label>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="consent"
              checked={form.requiresConsent === true}
              onChange={() => setForm({ ...form, requiresConsent: true })}
            />
            Yes
          </label>

          {form.requiresConsent && (
            <div className="mt-3 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 px-3 py-2 rounded-lg">
              Members will be asked for consent if this annexure is included
              in their report.
            </div>
          )}
        </div>
      </div>

      {/* TEXTAREA */}
      <div className="mt-8">
        <label
          className={clsx(
            "block text-sm font-medium mb-2",
            !form.body.trim() ? "text-red-500" : ""
          )}
        >
          Instructions for Iz *
        </label>

        <textarea
          maxLength={2000}
          disabled={loading}
          className="w-full border rounded-lg p-3 h-64 disabled:opacity-50 disabled:cursor-not-allowed"
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
        />
      </div>

      {/* -------------------------------------------------------- */}
      {/* DISCARD CHANGES DIALOG */}
      {/* -------------------------------------------------------- */}
      <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-zinc-800">
              Discard all changes?
            </Dialog.Title>

            <p className="mt-3 text-sm text-zinc-600">
              Are you sure you want to discard all the changes?
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                disabled={isSubmitting}
                onClick={() => setDiscardOpen(false)}
                className="rounded-md bg-zinc-200 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                No, go back
              </button>

              <button
                disabled={isSubmitting}
                onClick={() => {
                  setIsSubmitting(true);
                  navigate("/admin/annexures");
                }}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Yes, discard
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
